/**
 * RevisionOrchestrator - Huvudklass för att koordinera fullständiga revisioner
 * 
 * Ansvarig för:
 * - Validering av input data (schema, orgnr-match, datatyper)
 * - Skapande av batch-jobb för parallell bearbetning
 * - Kostnadskontroll och token-övervakning
 * - Koordinering mellan mini-analys och premium-rapport
 */

import { supabase } from '@/integrations/supabase/client';
import { RevisionConfig, RevisionStatus, BatchResult } from './types';
import { safeEstimateTokens, makeBatches } from '@/lib/utils/tokens';
import { MINI_MAX_INPUT_TOKENS, EST_TOKENS_PER_ITEM } from './config';
import { deployments } from '@/lib/azure-openai/env';

export class RevisionOrchestrator {
  private revisionId: string | null = null;
  private config: RevisionConfig | null = null;
  private miniMaxInputTokens = 30000; // MVP: Säker batch-storlek under Azure 100k TPM
  private maxConcurrentBatches = 1; // Ett API-anrop åt gången per revision (undviker rate limits)

  /**
   * Startar en ny revision med given konfiguration
   */
  async startRevision(
    config: RevisionConfig, 
    dataset: any[], 
    mapping?: Record<string, string>,
    companyConfig?: { companySize: 'small' | 'large'; vatReportingPeriod: 'monthly' | 'quarterly' }
  ): Promise<string> {
    try {
      console.log('🚀 Starting revision with config:', { orgNumber: config.orgNumber, companyName: config.companyName, companyConfig });
      
      // 1. Validera input
      this.validateInput(config, dataset);
      console.log('✅ Input validation passed');
      
      // 2. Beräkna deterministisk input hash för validering
      const inputDataHash = await this.calculateInputHash(dataset, mapping, companyConfig);
      console.log('🔐 Input data hash:', inputDataHash);
      
      // 3. Skapa revision i databas
      const userResponse = await supabase.auth.getUser();
      const userId = userResponse.data.user?.id;
      if (!userId) {
        throw new Error('Ingen användare inloggad. Logga in för att köra test.');
      }
      
      console.log('👤 User ID for revision:', userId);
      
      const { data: revision, error } = await supabase
        .from('revisions')
        .insert({
          user_id: userId,
          org_number: config.orgNumber,
          company_name: config.companyName,
          cost_cap_sek: config.costCapSek,
          status: 'initializing',
          input_data_hash: inputDataHash // ✅ Spara hash för deterministisk validering
        })
        .select()
        .single();

      if (error) throw error;
      
      this.revisionId = revision.id;
      this.config = config;

      // 4. Kontrollera om samma data körts tidigare (deterministisk validering)
      await this.checkDeterministicBehavior(inputDataHash);

      // 5. Skapa batch-jobb (token-budgeterad batching) med företagskonfiguration
      await this.createBatchJobs(dataset, mapping, companyConfig);

      // 6. Uppdatera status till batching
      await this.updateRevisionStatus('batching');

      const { data: createdBatches } = await supabase.from('batches').select('id').eq('revision_id', this.revisionId);
      
      // 7. Uppdatera status från "batching" till "processing" när batchar är skapade
      await supabase
        .from('revisions')
        .update({ 
          status: 'processing',
          updated_at: new Date().toISOString()
        })
        .eq('id', this.revisionId);
      
      console.log(`✅ Revision ${this.revisionId} startad med ${createdBatches?.length || 0} batchar (status: processing)`);
      return this.revisionId;

    } catch (error) {
      console.error('Fel vid start av revision:', error);
      if (this.revisionId) {
        await this.updateRevisionStatus('failed', (error as any).message);
      }
      throw error;
    }
  }

  /**
   * Hämtar aktuell status för en revision
   */
  async getRevisionStatus(revisionId: string): Promise<RevisionStatus> {
    const { data: revision, error } = await supabase
      .from('revisions')
      .select('*')
      .eq('id', revisionId)
      .single();

    if (error) throw error;

    // Hämta batch-progress
    const { data: batches } = await supabase
      .from('batches')
      .select('state, module, retry_after, last_error')
      .eq('revision_id', revisionId);

    const batchProgress = this.calculateBatchProgress(batches || []);
    const moduleProgress = this.calculateModuleProgress(batches || []);

    // Beräkna nästa retry-tid från batcharnas retry_after
    const nowIso = new Date().toISOString();
    const nextRetryAfter = (batches || [])
      .filter((b: any) => (b.state === 'rate_limited' || b.state === 'pending') && typeof b.retry_after === 'string' && b.retry_after > nowIso)
      .map((b: any) => b.retry_after as string)
      .sort()[0];

    return {
      id: revision.id,
      status: revision.status as 'initializing' | 'batching' | 'processing' | 'aggregating' | 'reporting' | 'completed' | 'failed' | 'cancelled',
      progress: {
        batches_total: batchProgress.total,
        batches_completed: batchProgress.progressCompleted || batchProgress.completed,
        modules_total: moduleProgress.total,
        modules_completed: moduleProgress.completed
      },
      costs: {
        total_cost_sek: typeof revision.total_cost_sek === 'string' ? parseFloat(revision.total_cost_sek) : revision.total_cost_sek,
        cost_cap_sek: typeof revision.cost_cap_sek === 'string' ? parseFloat(revision.cost_cap_sek) : revision.cost_cap_sek,
        tokens_in: Number(revision.total_tokens_in),
        tokens_out: Number(revision.total_tokens_out)
      },
      started_at: revision.started_at,
      estimated_completion: this.estimateCompletion(batchProgress, revision.started_at),
      next_retry_after: nextRetryAfter
    };
  }

  /**
   * Revision-baserad kö-position: Räknar hela revisioner som en enhet istället för individuella jobb
   * Detta fixar problemet där kö-positionen hoppar när en ny batch startar
   */
  async getQueuePosition(revisionId: string): Promise<{
    position: number;
    estimated_wait_minutes: number;
    total_tpm: number;
  }> {
    // Hämta första jobbet för denna revision (äldsta created_at)
    const { data: myFirstJob } = await supabase
      .from('worker_queue')
      .select('created_at, job_id')
      .like('job_id', `${revisionId}%`)
      .in('status', ['pending', 'processing'])
      .order('created_at', { ascending: true })
      .limit(1);

    if (!myFirstJob || myFirstJob.length === 0) {
      return { position: 0, estimated_wait_minutes: 0, total_tpm: 0 };
    }

    const myRevisionCreatedAt = myFirstJob[0].created_at;

    // Räkna REVISIONER före mig (inte individuella jobb)
    // Hämta alla jobb före min revision, extrahera revision_id från job_id
    const { data: allJobsAhead } = await supabase
      .from('worker_queue')
      .select('job_id, estimated_tokens, created_at')
      .in('status', ['pending', 'processing'])
      .eq('model_type', deployments.mini)
      .lt('created_at', myRevisionCreatedAt);

    // Gruppera per revision (extrahera revision_id från job_id)
    // job_id format: "<revision_id>_batch_1" eller "<revision_id>_aggregate_..."
    const revisionsAhead = new Map<string, number>();
    (allJobsAhead || []).forEach(job => {
      // Extrahera revision_id från job_id (allt före första "_batch" eller "_aggregate")
      const revisionIdMatch = job.job_id?.match(/^([^_]+(?:_[^_]+)*?)_(?:batch|aggregate|final)/);
      const extractedRevisionId = revisionIdMatch ? revisionIdMatch[1] : job.job_id;
      
      if (extractedRevisionId) {
        const current = revisionsAhead.get(extractedRevisionId) || 0;
        revisionsAhead.set(extractedRevisionId, current + (job.estimated_tokens || 50000));
      }
    });

    const totalTokensAhead = Array.from(revisionsAhead.values()).reduce((sum, tokens) => sum + tokens, 0);
    const revisionsAheadCount = revisionsAhead.size;

    // Hämta total TPM från alla aktiva deployments
    const { data: deploymentRows } = await supabase
      .from('ai_deployments')
      .select('max_tokens_per_minute')
      .eq('model_type', deployments.mini)
      .eq('is_active', true);

    const totalTPM = (deploymentRows || []).reduce(
      (sum, d) => sum + (d.max_tokens_per_minute || 0), 0
    ) || 100000; // Fallback: 100k TPM om tabellen är tom
    
    console.log(`📊 Queue position: ${revisionsAheadCount} revisioner före mig, ${totalTokensAhead} tokens, ${totalTPM} TPM`);

    // Estimerad väntetid baserat på totala tokens och TPM
    const estimatedMinutes = Math.max(1, Math.ceil(totalTokensAhead / totalTPM));
    
    // Position = antal REVISIONER före + 1
    const position = revisionsAheadCount + 1;

    return { position, estimated_wait_minutes: estimatedMinutes, total_tpm: totalTPM };
  }

  /**
   * Lyssnar på batch_done events och triggar aggregering
   */
  async onBatchCompleted(batchId: string): Promise<void> {
    if (!this.revisionId) return;

    try {
      // Kontrollera om alla batchar för en modul är klara (completed ELLER failed)
      const { data: batches } = await supabase
        .from('batches')
        .select('module, state')
        .eq('revision_id', this.revisionId);

      if (!batches) return;

      // Gruppera per modul
      const moduleStatus = this.groupBatchesByModule(batches);
      
      // Trigga aggregering för moduler där alla batchar är completed eller failed
      for (const [module, status] of Object.entries(moduleStatus)) {
        if (status.allDone && status.total > 0) {
          // Kontrollera om aggregering redan finns
          const { data: existingAgg } = await supabase
            .from('aggregates')
            .select('id')
            .eq('revision_id', this.revisionId)
            .eq('module', module)
            .single();
          
          if (!existingAgg) {
            await this.triggerModuleAggregation(module);
          }
        }
      }

      // Hantera edge case: inga batchar eller inga moduler => trigga rapport direkt
      const moduleNames = [...new Set((batches || []).map(b => b.module))];
      if (!moduleNames || moduleNames.length === 0) {
        console.log(`⚠️ Revision ${this.revisionId}: Inga moduler/batcher hittades, triggar rapport direkt`);
        await this.triggerFinalReport();
        return;
      }

      // Kontrollera om hela revisionen är klar genom att kolla aggregates-tabellen
      const modules = [...new Set(batches.map(b => b.module))];
      
      // Graceful degradation: Räkna successful vs failed/rate-limited batches
      const completedCount = batches.filter(b => b.state === 'completed').length;
      const failedCount = batches.filter(b => b.state === 'failed').length;
      const rateLimitedCount = batches.filter(b => b.state === 'rate_limited').length;
      const totalCount = batches.length;
      
      // KRITISKT: Om ALLA batchar är permanent failed (inte rate_limited) => sätt revision till failed
      const allBatchesPermanentlyFailed = batches.every(b => b.state === 'failed') && failedCount > 0;
      if (allBatchesPermanentlyFailed) {
        console.error(`❌ Revision ${this.revisionId}: Alla ${totalCount} batchar permanent failed – markerar revision som failed`);
        await this.updateRevisionStatus('failed', 'All batches permanently failed - could not complete analysis');
        return;
      }
      
      // Graceful degradation: Om majoriteten (>80%) lyckades, fortsätt med partiella resultat
      const successRate = completedCount / totalCount;
      if (successRate >= 0.8 && completedCount > 0) {
        console.log(`✅ Revision ${this.revisionId}: Tillräcklig data (${Math.round(successRate * 100)}%) – fortsätter med partiell analys`);
        console.warn(`⚠️ ${failedCount} batchar failed, ${rateLimitedCount} rate-limited – rapport kommer att innehålla varning`);
      } else if (completedCount > 0 && successRate < 0.8) {
        console.warn(`⚠️ Revision ${this.revisionId}: Låg success rate ${Math.round(successRate * 100)}% – ${completedCount}/${totalCount} batchar`);
        console.log(`🔄 Fortsätter med degraderat läge – rapport kommer markeras som incomplete`);
      }
      
      const { data: aggregates } = await supabase
        .from('aggregates')
        .select('module')
        .eq('revision_id', this.revisionId);
      
      const allModulesAggregated = modules.every(module => 
        aggregates?.some(agg => agg.module === module)
      );

      if (allModulesAggregated && modules.length > 0) {
        // Kontrollera om rapport redan finns
        const { data: existingReport } = await supabase
          .from('revision_reports')
          .select('id')
          .eq('revision_id', this.revisionId)
          .single();
        
        if (!existingReport) {
          await this.triggerFinalReport();
        }
      }

    } catch (error) {
      console.error('Fel vid batch completion hantering:', error);
    }
  }

  /**
   * Kontrollerar om revision är klar för rapport och triggar om så
   */
  async checkAndTriggerReportIfReady(revisionId: string): Promise<boolean> {
    try {
      // Hämta alla batchar för revisionen
      const { data: batches } = await supabase
        .from('batches')
        .select('module, state')
        .eq('revision_id', revisionId);

      if (!batches || batches.length === 0) return false;

      // Gruppera per modul och kontrollera om alla är klara
      const moduleStatus = this.groupBatchesByModule(batches);
      const modules = Object.keys(moduleStatus);
      
      // Kontrollera om alla moduler har sina batchar klara
      const allBatchesDone = modules.every(module => moduleStatus[module].allDone);
      
      if (!allBatchesDone) {
        console.log(`⏳ Revision ${revisionId}: Väntar på batchar att bli klara`);
        return false;
      }

      console.log(`✅ Revision ${revisionId}: Alla batchar är klara`);
      
      // Graceful degradation: Räkna successful vs failed batches
      const completedCount = modules.reduce((sum, mod) => sum + moduleStatus[mod].completed, 0);
      const failedCount = modules.reduce((sum, mod) => sum + moduleStatus[mod].failed, 0);
      const totalCount = completedCount + failedCount;
      
      // KRITISKT: Om ALLA batchar är failed => markera revision som failed
      const allBatchesFailed = Object.keys(moduleStatus).every(module => 
        moduleStatus[module].failed === moduleStatus[module].total && moduleStatus[module].total > 0
      );
      if (allBatchesFailed) {
        console.error(`❌ Revision ${revisionId}: Alla ${totalCount} batchar failed – markerar revision som failed`);
        await this.updateRevisionStatus('failed', 'All batches failed - could not complete analysis');
        return false;
      }
      
      // Graceful degradation: Fortsätt även om vissa batchar failade
      if (failedCount > 0 && completedCount > 0) {
        const successRate = Math.round((completedCount / totalCount) * 100);
        console.warn(`⚠️ Revision ${revisionId}: Partial success - ${completedCount}/${totalCount} batchar lyckades (${successRate}%), ${failedCount} misslyckades`);
        console.log(`🔄 Fortsätter med aggregering av tillgänglig data från ${completedCount} completed batches`);
      }

      // Kontrollera aggregates för varje modul
      for (const module of modules) {
        const { data: existingAgg } = await supabase
          .from('aggregates')
          .select('id')
          .eq('revision_id', revisionId)
          .eq('module', module)
          .single();
        
        if (!existingAgg) {
          console.log(`📊 Triggar aggregering för modul: ${module}`);
          await this.triggerModuleAggregation(module);
        }
      }

      // Hämta aggregates för att se om alla moduler är aggregerade
      const { data: aggregates } = await supabase
        .from('aggregates')
        .select('module')
        .eq('revision_id', revisionId);
      
      const allModulesAggregated = modules.every(module => 
        aggregates?.some(agg => agg.module === module)
      );

      if (!allModulesAggregated) {
        console.log(`⏳ Revision ${revisionId}: Väntar på aggregering`);
        return false;
      }

      console.log(`✅ Revision ${revisionId}: Alla moduler aggregerade`);

      // Kontrollera om rapport redan finns
      const { data: existingReport } = await supabase
        .from('revision_reports')
        .select('id')
        .eq('revision_id', revisionId)
        .single();
      
      if (existingReport) {
        console.log(`📄 Revision ${revisionId}: Rapport finns redan`);
        return true;
      }

      // Trigga rapportgenerering
      console.log(`🎯 Triggar rapportgenerering för revision ${revisionId}`);
      await this.triggerFinalReport();
      return true;

    } catch (error) {
      console.error(`❌ Fel vid kontroll av revision ${revisionId}:`, error);
      return false;
    }
  }

  /**
   * Kostnadskontroll - kontrollerar om vi närmar oss gränsen
   */
  async checkCostLimits(): Promise<boolean> {
    if (!this.revisionId || !this.config) return true;

    const status = await this.getRevisionStatus(this.revisionId);
    const costPercentage = status.costs.total_cost_sek / status.costs.cost_cap_sek;

    if (costPercentage > 0.9) {
      console.warn(`Kostnadsgräns nästan nådd: ${costPercentage * 100}%`);
      await this.activateCostSavingMode();
      return false;
    }

    return true;
  }

  // === PRIVATE METHODS ===

  private validateInput(config: RevisionConfig, dataset: any[]): void {
    if (!config.orgNumber.match(/^\d{6}-\d{4}$/)) {
      throw new Error('Ogiltigt organisationsnummer format');
    }

    if (dataset.length === 0) {
      throw new Error('Dataset är tomt');
    }

    if (config.costCapSek <= 0) {
      throw new Error('Kostnadsgräns måste vara positiv');
    }

    // Validera att dataset har rätt struktur
    const firstRow = dataset[0];
    if (!firstRow || typeof firstRow !== 'object') {
      throw new Error('Dataset har felaktig struktur');
    }
  }

  private async createBatchJobs(
    dataset: any[], 
    mapping?: Record<string, string>,
    companyConfig?: { companySize: 'small' | 'large'; vatReportingPeriod: 'monthly' | 'quarterly' }
  ): Promise<void> {
    // Token-budgeterad batching: använd makeBatches för att skapa batchar inom tokenbudget
    const tokenBudgetedBatches = makeBatches(dataset, MINI_MAX_INPUT_TOKENS, EST_TOKENS_PER_ITEM);
    console.log(`🎯 Skapade ${tokenBudgetedBatches.length} token-budgeterade batchar från ${dataset.length} rader`);
    
    // Extrahera headers från första raden om dataset är objekt
    let headers: string[] | undefined = undefined;
    if (dataset.length > 0 && typeof dataset[0] === 'object' && !Array.isArray(dataset[0])) {
      headers = Object.keys(dataset[0]);
      console.log(`📋 Extraherade headers från dataset:`, headers);
    }
    
    const batches = tokenBudgetedBatches.map((batchData, i) => {
      const batchId = `${this.revisionId}_batch_${i + 1}`;
      const estimatedTokens = safeEstimateTokens(batchData);
      console.log(`📦 Batch ${i + 1}: ${batchData.length} rader, ~${estimatedTokens} tokens`);
      
      return {
        batch_id: batchId,
        revision_id: this.revisionId,
        module: 'skattekonto', // TODO: Dynamisk modul-detection
        batch_no: i + 1,
        data_sample: batchData,
        state: 'queued'
      };
    });

    const { error } = await supabase
      .from('batches')
      .insert(batches);

    if (error) throw error;

    // Lägg batch-jobb i worker-kön med mappning, företagskonfiguration OCH headers
    await this.queueBatchJobs(batches, mapping, companyConfig, headers);
  }

  private async queueBatchJobs(
    batches: any[], 
    mapping?: Record<string, string>,
    companyConfig?: { companySize: 'small' | 'large'; vatReportingPeriod: 'monthly' | 'quarterly' },
    headers?: string[]
  ): Promise<void> {
    const queueJobs = batches.map(batch => {
      const estimatedTokens = safeEstimateTokens(batch.data_sample);
      return {
        queue_name: 'batch_analysis',
        job_id: batch.batch_id,
        job_type: 'batch',
        module_name: 'skattekonto', // Token-baserad kö: modul
        model_type: 'gpt-4.1-mini', // Token-baserad kö: modell
        estimated_tokens: estimatedTokens, // Token-baserad kö: estimerade tokens
        payload: {
          batch_id: batch.batch_id,
          revision_id: this.revisionId,
          module: batch.module,
          data: batch.data_sample,
          field_mapping: mapping || null,
          company_config: companyConfig || { companySize: 'small', vatReportingPeriod: 'monthly' },
          headers: headers || null
        },
        priority: 1,
        status: 'pending'
      };
    });

    const { data: queued, error } = await supabase
      .from('worker_queue')
      .insert(queueJobs)
      .select();

    if (error) throw error;
    console.log(`📨 Lagt ${queued?.length || 0} jobb i kön för revision ${this.revisionId}${mapping ? ' (med fältmappning)' : ''}${companyConfig ? ' (med företagskonfig)' : ''}${headers ? ' (med headers)' : ''}`);
  }

  // Removed getBatchCount - no longer needed with token-budgeted batching

  private async updateRevisionStatus(status: 'initializing' | 'batching' | 'processing' | 'aggregating' | 'reporting' | 'completed' | 'failed' | 'cancelled', errorMessage?: string): Promise<void> {
    if (!this.revisionId) return;

    const updates: any = { status, updated_at: new Date().toISOString() };
    if (errorMessage) updates.last_error = errorMessage;
    if (status === 'completed') updates.completed_at = new Date().toISOString();

    await supabase
      .from('revisions')
      .update(updates)
      .eq('id', this.revisionId);
  }

  private calculateBatchProgress(batches: any[]) {
    const total = batches.length;
    // Räkna completed och processing/rate_limited som aktiva (inte bara completed)
    const completed = batches.filter(b => b.state === 'completed').length;
    const active = batches.filter(b => ['processing', 'rate_limited'].includes(b.state)).length;
    
    // För progress-visning: visa aktiva som delvis completed (förhindrar 0%)
    const progressCompleted = completed + (active * 0.5); // Rate-limited = 50% progress
    
    return { 
      total, 
      completed,
      progressCompleted: Math.min(progressCompleted, total) 
    };
  }

  private calculateModuleProgress(batches: any[]) {
    const modules = new Set(batches.map(b => b.module));
    let completedModules = 0;

    for (const module of modules) {
      const moduleBatches = batches.filter(b => b.module === module);
      const allCompleted = moduleBatches.every(b => b.state === 'completed');
      if (allCompleted && moduleBatches.length > 0) {
        completedModules++;
      }
    }

    return { total: modules.size, completed: completedModules };
  }

  private estimateCompletion(progress: any, startedAt: string): string | undefined {
    if (progress.completed === 0) return undefined;
    
    const elapsed = Date.now() - new Date(startedAt).getTime();
    const remaining = progress.total - progress.completed;
    const avgTimePerBatch = elapsed / progress.completed;
    const estimatedRemaining = remaining * avgTimePerBatch;
    
    return new Date(Date.now() + estimatedRemaining).toISOString();
  }

  private groupBatchesByModule(batches: any[]) {
    const result: Record<string, any> = {};
    
    for (const batch of batches) {
      if (!result[batch.module]) {
        result[batch.module] = { total: 0, completed: 0, failed: 0, allDone: false };
      }
      result[batch.module].total++;
      if (batch.state === 'completed') {
        result[batch.module].completed++;
      }
      if (batch.state === 'failed') {
        result[batch.module].failed++;
      }
    }

    // Modulen är "klar" om alla batchar är completed ELLER failed
    for (const module of Object.keys(result)) {
      const completedOrFailed = result[module].completed + result[module].failed;
      result[module].allDone = result[module].total === completedOrFailed;
    }

    return result;
  }

  private async triggerModuleAggregation(module: string): Promise<void> {
    // Lägg aggregering-jobb i kön
    const { error } = await supabase
      .from('worker_queue')
      .insert({
        queue_name: 'batch_analysis',
        job_id: `${this.revisionId}_aggregate_${module}`,
        job_type: 'aggregate',
        payload: {
          revision_id: this.revisionId,
          module: module
        },
        priority: 2
      });

    if (error) {
      console.error('Kunde inte skapa aggregering-jobb:', error);
    }
  }

  private async triggerFinalReport(): Promise<void> {
    if (!this.revisionId) return;

    // Fetch current revision status to avoid flipping completed -> reporting
    const { data: rev } = await supabase
      .from('revisions')
      .select('status')
      .eq('id', this.revisionId)
      .single();

    // If already completed, do nothing
    if (rev?.status === 'completed') {
      console.log(`📄 Revision ${this.revisionId} already completed, skipping final report trigger`);
      return;
    }

    // Idempotency guard 1: report already exists
    const { data: existingReport } = await supabase
      .from('revision_reports')
      .select('id')
      .eq('revision_id', this.revisionId)
      .maybeSingle();

    if (existingReport) {
      console.log(`📄 Revision ${this.revisionId}: report already exists, skipping enqueue`);
      // Ensure status is completed if report exists
      await this.updateRevisionStatus('completed');
      return;
    }

    // Idempotency guard 2: job already queued/in progress
    const { data: existingJob } = await supabase
      .from('worker_queue')
      .select('job_id, status')
      .eq('queue_name', 'premium_report')
      .contains('payload', { revision_id: this.revisionId } as any)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existingJob && existingJob.status !== 'failed') {
      console.log(`⏭️ Report job already queued (${existingJob.job_id}), skip enqueue`);
      if (rev?.status !== 'reporting') {
        await this.updateRevisionStatus('reporting');
      }
      return;
    }

    // Move to reporting state (only if not already completed)
    if (rev?.status !== 'reporting') {
      await this.updateRevisionStatus('reporting');
    }

    // Use unique job id to avoid duplicates and React key collisions in diagnostics
    const jobId = `${this.revisionId}_final_report_${Date.now()}`;
    const { error } = await supabase
      .from('worker_queue')
      .insert({
        queue_name: 'premium_report',
        job_id: jobId,
        job_type: 'report',
        payload: { revision_id: this.revisionId },
        priority: 3
      });

    if (error) {
      console.error('Kunde inte skapa rapport-jobb:', error);
    } else {
      console.log(`🧾 Triggade slutrapport-jobb ${jobId} för revision ${this.revisionId}`);
    }
  }

  /**
   * Beräknar SHA-256 hash av input-data för deterministisk validering
   * Samma data ska alltid ge samma hash
   */
  private async calculateInputHash(
    dataset: any[], 
    mapping?: Record<string, string>,
    companyConfig?: { companySize: 'small' | 'large'; vatReportingPeriod: 'monthly' | 'quarterly' }
  ): Promise<string> {
    // Sortera dataset för att garantera konsistent hash oavsett ordning
    const sortedDataset = [...dataset].sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    
    // Bygg hashable string med alla input-parametrar
    const inputString = JSON.stringify({
      dataset: sortedDataset,
      mapping: mapping || null,
      companyConfig: companyConfig || null
    });
    
    // Använd Web Crypto API för SHA-256
    const encoder = new TextEncoder();
    const data = encoder.encode(inputString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    return hashHex;
  }

  /**
   * Kontrollerar om samma input-data körts tidigare och varnar om olika resultat
   * Detta upptäcker icke-deterministiskt AI-beteende
   */
  private async checkDeterministicBehavior(inputHash: string): Promise<void> {
    // Hämta tidigare revisioner med samma hash
    const { data: previousRevisions } = await supabase
      .from('revisions')
      .select('id, org_number, company_name')
      .eq('input_data_hash', inputHash)
      .eq('status', 'completed')
      .neq('id', this.revisionId!) // Exkludera nuvarande revision
      .order('created_at', { ascending: false })
      .limit(5);

    if (!previousRevisions || previousRevisions.length === 0) {
      console.log('✅ Första körningen med denna data - ingen tidigare hash matchning');
      return;
    }

    console.log(`🔍 Hittade ${previousRevisions.length} tidigare körningar med samma input-data`);

    // Hämta aggregates för tidigare körningar
    const prevRevisionIds = previousRevisions.map(r => r.id);
    const { data: prevAggregates } = await supabase
      .from('aggregates')
      .select('revision_id, flag_count')
      .in('revision_id', prevRevisionIds);

    if (prevAggregates && prevAggregates.length > 0) {
      const prevFlagCounts = prevAggregates.map(a => a.flag_count);
      const uniqueCounts = new Set(prevFlagCounts);

      if (uniqueCounts.size > 1) {
        console.error('🚨 ICKE-DETERMINISTISKT BETEENDE UPPTÄCKT!', {
          inputHash,
          previousFlagCounts: Array.from(uniqueCounts),
          message: 'Samma input-data gav olika antal fynd vid olika körningar!'
        });
      } else {
        console.log(`✅ Deterministiskt beteende verifierat: Alla tidigare körningar gav ${prevFlagCounts[0]} fynd`);
      }
    }
  }

  private async activateCostSavingMode(): Promise<void> {
    console.log('Aktiverar kostnadssparläge');
    
    // Implementera kostnadssparande strategier:
    // - Skippa lågriskregler
    // - Korta ner top-N fynd
    // - Stoppa extra analyser
    
    // TODO: Implementera specifika åtgärder
  }
}